import { Link, NavLink } from "react-router-dom";
import { SolutionsMegaMenu } from "@/components/navbar/SolutionsMegaMenu";
import { CalyroLogo } from "@/components/logo/CalyroLogo";
import { useState } from "react";

const navLinkClass = "text-sm font-medium text-slate-700 hover:text-slate-900 transition";

export function MainNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-sky-200/80 bg-white/95 backdrop-blur-xl shadow-sm relative overflow-hidden">
      {/* Circuit Board background pattern for navbar */}
      <div className="pointer-events-none absolute inset-0">
        {/* Main circuit grid - Blue lines */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(59,130,246,0.15)_1px,transparent_1px),linear-gradient(to_bottom,rgba(59,130,246,0.15)_1px,transparent_1px)] bg-[size:40px_40px]" />
        
        {/* Secondary grid - Purple offset */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.12)_1px,transparent_1px)] bg-[size:80px_80px] translate-x-[20px] translate-y-[20px]" />
        
        {/* Circuit nodes - Blue */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.25)_1.5px,transparent_1.5px)] bg-[size:40px_40px]" />
        
        {/* Larger accent nodes - Purple */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.3)_2px,transparent_2px)] bg-[size:120px_120px] translate-x-[60px] translate-y-[60px]" />
        
        {/* Diagonal traces */}
        <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_38px,rgba(59,130,246,0.08)_38px,rgba(59,130,246,0.08)_40px)]" />
        <div className="absolute inset-0 bg-[repeating-linear-gradient(-45deg,transparent,transparent_38px,rgba(139,92,246,0.08)_38px,rgba(139,92,246,0.08)_40px)]" />
      </div>
      
      {/* Navbar content with relative positioning to stay above background */}
      <div className="relative mx-auto max-w-6xl px-4 py-5 lg:px-6">
        <div className="flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <CalyroLogo className="h-16 md:h-16" showText={true} textClassName="text-xl md:text-2xl font-semibold" transparent={true} />
          </Link>

          {/* Center: Desktop Nav Links */}
          <nav className="hidden items-center gap-6 text-sm text-slate-700 md:flex">
            <NavLink to="/features" className={navLinkClass}>
              Features
            </NavLink>

            <SolutionsMegaMenu />

            <NavLink to="/pricing" className={navLinkClass}>
              Pricing
            </NavLink>

            <NavLink to="/faq" className={navLinkClass}>
              FAQ
            </NavLink>
          </nav>

          {/* Right: Auth Buttons */}
          <div className="flex items-center gap-3">
            <NavLink
              to="/customer-login"
              className="hidden text-sm font-medium text-slate-700 hover:text-slate-900 md:block"
            >
              Log in
            </NavLink>
            <NavLink
              to="/pricing"
              className="hidden md:block rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:shadow-lg hover:scale-105 transition-all"
            >
              Get Started
            </NavLink>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4">
            <div className="flex flex-col space-y-3">
              <NavLink
                to="/features"
                className="text-base font-medium text-slate-700 hover:text-slate-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </NavLink>
              <NavLink
                to="/pricing"
                className="text-base font-medium text-slate-700 hover:text-slate-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </NavLink>
              <NavLink
                to="/faq"
                className="text-base font-medium text-slate-700 hover:text-slate-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </NavLink>
              <div className="border-t border-slate-800/50 pt-3 mt-3 flex flex-col space-y-3">
                <NavLink
                  to="/customer-login"
                  className="text-base font-medium text-slate-700 hover:text-slate-900 py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Log in
                </NavLink>
                <NavLink
                  to="/pricing"
                  className="w-full text-center rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 text-base font-semibold text-white shadow-md hover:shadow-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </NavLink>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
